import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { trackCTA } from '../utils/analytics';
import { useLanguage } from '../context/LanguageContext';
import { content } from '../i18n/content';

const Hero = () => {
  const { language } = useLanguage();
  const t = content[language].hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });
  const yParallax = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const opacityParallax = useTransform(scrollYProgress, [0, 1], [1, 0]);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const fadeInDelay = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, delay: 0.2 }
  };

  return (
    <section id="hero" ref={ref} className="min-h-screen flex items-center relative premium-bg-1 pt-20">
      <motion.div
        className="container mx-auto px-4 md:px-16 lg:px-24 z-10 premium-content-layer flex flex-col"
        style={{ y: yParallax, opacity: opacityParallax }}
      >
        <div className="w-full lg:w-[85%] xl:w-[75%] text-left">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6 backdrop-blur-sm"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse"></span>
            <span className="text-white/80 text-xs md:text-sm font-medium tracking-wide">{t.badge}</span>
          </motion.div>

          <motion.h1
            className="font-display text-3xl md:text-5xl lg:text-6xl font-bold mb-5 leading-[1.15] pb-1 relative"
            {...fadeInUp}
          >
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-white to-white/40 animate-[shimmer_4s_infinite] bg-[length:200%_auto]">
              {t.titleLine1}
            </span>
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold via-yellow-200 to-gold animate-[shimmer_3s_infinite] bg-[length:200%_auto]">{t.titleLine2}</span>
          </motion.h1>

          <motion.p
            className="text-base md:text-lg text-light-gray/70 font-light mb-8 max-w-xl leading-relaxed"
            {...fadeInDelay}
          >
            {t.subtitle}
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-3 mb-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <RouterLink
              to="/empezar"
              onClick={() => trackCTA('hero', 'Agendar diagnóstico gratuito - /empezar')}
              className="px-6 py-3 bg-secondary text-white font-label font-medium text-sm md:text-base rounded-full shadow-[0_0_20px_rgba(0,123,255,0.2)] hover:shadow-[0_0_30px_rgba(0,123,255,0.4)] transition-all duration-300 transform hover:-translate-y-1 cursor-pointer flex items-center justify-center gap-2"
            >
              {t.ctaPrimary}
            </RouterLink>
            <a
              href="#trust"
              onClick={() => trackCTA('hero', 'Conocer cómo trabajamos')}
              className="px-6 py-3 bg-transparent border border-white/20 text-white font-label font-medium text-sm md:text-base rounded-full hover:bg-white/5 transition-all duration-300 flex items-center justify-center"
            >
              {t.ctaSecondary}
            </a>
          </motion.div>

          <motion.div
            className="flex flex-col sm:flex-row flex-wrap gap-x-5 gap-y-2 text-white/60 text-xs md:text-sm font-light"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {t.checklist.map((item, i) => (
              <div key={i} className="flex items-center gap-2"><span className="text-secondary">✓</span> {item}</div>
            ))}
          </motion.div>

          <motion.p
            className="text-white/45 text-xs md:text-sm font-light mt-6 pt-5 border-t border-white/10 max-w-xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            {t.idealFor}
          </motion.p>
        </div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-10 right-10 md:right-24 hidden md:block"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.8 }}
      >
        <div className="flex items-center gap-4">
          <motion.div
            className="w-10 h-10 border border-white/20 rounded-full flex justify-center items-center backdrop-blur-sm"
            animate={{ y: [0, 5, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <span className="text-secondary">↓</span>
          </motion.div>
          <span className="text-white/50 text-sm font-light uppercase tracking-widest">{t.scroll}</span>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;

